<template>
    <div class="main">
        <div class="body-content">
            <div class="page-header">
                <h1>Vessel Capacity</h1>
            </div>
            <div class="form-group">
                <div class="form-item full-width">
                    <label for="booking_no">Booking Number:</label>
                    <textarea id="booking_no" v-model="bookingNo" placeholder="Enter Booking Number"
                        @keyup.enter="getVesselCapacity" rows="3"></textarea>
                </div>
                <div class="form-item button-container">
                    <button type="button" class="excel-button" :disabled="isSubmitting" @click="getVesselCapacity">
                        {{ isSubmitting ? 'Getting Capacity...' : 'Get Capacity' }}
                    </button>
                </div>
            </div>

            <div v-if="loading" class="loading">
                <p>Loading vessel capacity...</p>
            </div>

            <div v-if="capacityData && !loading" class="capacity-info">
                <h4>Vessel Capacity Information</h4>
                <div class="info-grid">
                    <div class="info-item" v-for="(value, key) in capacityData" :key="key">
                        <strong>{{ formatKey(key) }}:</strong> {{ value }}
                    </div>
                </div>
            </div>

            <div v-if="error" class="error">
                <p>{{ error }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { saveAs } from 'file-saver';

export default {
    name: 'VesselCapacity',
    data() {
        return {
            isSubmitting: false,
            bookingNo: '',
            capacityData: null,
            loading: false,
            error: null
        };
    },
    methods: {
        async getVesselCapacity() {
            // Prevent multiple submissions
            if (this.isSubmitting) {
                return;
            }
            
            if (!this.bookingNo.trim()) {
                useToast().error('Please enter a booking number');
                return;
            }

            // Set loading state
            this.isSubmitting = true;
            this.loading = true;
            this.error = null;
            this.capacityData = null;

            try {
                const token = localStorage.getItem('access_token');
                const config = {
                    headers: {
                        'accept': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    responseType: 'blob' // Handle binary response
                };
                const data = new FormData();
                data.append('booking_no', this.bookingNo);
                const response = await axios.post('/getvesselcapacity', data, config);
                console.log('api response', response);
                console.log('API Response Headers:', response.headers);
                console.log('Content-Disposition Header:', response.headers['content-disposition']);

                if (response.headers['content-type'] == 'application/json') {
                    try {
                        const text = await response.data.text();
                        const jsonData = JSON.parse(text);
                        this.error = jsonData[1] || 'No data found';
                        useToast().error(this.error);
                    } catch (parseError) {
                        this.error = 'Unable to parse response';
                        useToast().error(this.error);
                    }
                } else {
                    // Handle FileResponse (file download)
                    const blob = new Blob([response.data]);
                    console.log('Content-Disposition Header:', response.headers['content-disposition']);

                    // Create filename with booking number since content-disposition is undefined
                    let filename = 'vessel_capacity.xlsx';
                    if (response.headers['content-disposition']) {
                        filename = response.headers['content-disposition']
                            .split('filename=')[1]?.replace(/"/g, '') || filename;
                    } else {
                        // Use current datetime in filename since header is undefined
                        const now = new Date();
                        const datetime = now.toISOString().slice(0, 19).replace(/:/g, '').replace(/-/g, '').replace('T', '_');
                        filename = `vessel_capacity_${datetime}.xlsx`;
                    }
                    console.log('Extracted filename:', filename);
                    saveAs(blob, filename);
                    useToast().success('File downloaded successfully');
                }
            } catch (error) {
                console.error('Error fetching vessel capacity:', error);
                this.error = error.response?.data?.detail || error.message || 'Error fetching vessel capacity';
                useToast().error(this.error);
            } finally {
                // Always reset loading state
                this.loading = false;
                this.isSubmitting = false;
            }
        },
        formatKey(key) {
            // Convert snake_case or camelCase to Title Case
            return key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        }
    },
    mounted() {
        // Optionally load data on component mount
        // this.getVesselCapacity();
    }
}
</script>

<style scoped>
.form-group {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
}

.form-item {
    display: flex;
    flex-direction: column;
}

.form-item.full-width {
    width: 100%;
}

.form-item.button-container {
    align-items: flex-start;
}

.form-item label {
    margin-bottom: 5px;
    font-weight: bold;
}

.form-item input,
.form-item textarea {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
}

.form-item.full-width textarea {
    width: 100%;
}

.form-item input {
    width: 200px;
}

.excel-button {
    background-color: #17a2b8;
    color: white;
    border: none;
    padding: 10px 15px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s;
}

.excel-button:hover {
    background-color: #138496;
}

.excel-button:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
}

.excel-button:disabled:hover {
    background-color: #6c757d;
}

.capacity-info {
    margin-top: 20px;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background-color: #f9f9f9;
}

.capacity-info h4 {
    margin-top: 0;
    color: #333;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
    margin-top: 15px;
}

.info-item {
    padding: 12px;
    background-color: white;
    border-radius: 4px;
    border: 1px solid #eee;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.info-item strong {
    color: #555;
}

.loading {
    text-align: center;
    padding: 20px;
    margin-top: 20px;
    font-style: italic;
    color: #666;
}

.error {
    color: #d32f2f;
    background-color: #ffebee;
    border: 1px solid #f44336;
    border-radius: 5px;
    padding: 15px;
    margin-top: 20px;
    text-align: center;
}
</style>